import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FolderOpen, FolderPlus, Upload, Search, Grid, List, Download, X, Calendar, Tag, MessageSquare, Type, Star, Users, MapPin, Sparkles, Settings, Play, Wand2, Clock, Split, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Loader2, Trash2, Pencil, Save, Cloud, Filter, ArrowUpRight, Maximize2, Archive, Paintbrush, Target, Plus, Home, FolderInput, Check, Crop, Eraser } from 'lucide-react';
import JSZip from 'jszip';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/helpers';
import {
  loadLibraryItems,
  toggleFavorite,
  deleteLibraryItem,
  getBrandTags,
  renameTag,
  deleteTagFromAll,
  updateItemTags,
  updateLibraryItem,
  addRefinedVersion,
  setActiveVersion,
  splitHistoryVersion,
  deleteHistoryVersion,
  uploadLibraryImage,
  saveOneDriveItems,
  updateCarouselCardImage,
  saveLibraryItem,
  addUpscaledVersion,
  addUpscaledCarouselCard,
  createFolder,
  getFoldersWithThumbnails,
  updateFolder,
  deleteFolder,
  moveItemsToFolder,
  getItemsInFolder,
  moveFolderToFolder,
  getFolderPath,
  getAllItemsInFolderRecursive
} from '../services/libraryService';
import { checkOneDriveConnection } from '../services/oneDriveService';
import { getPillarsByBrand } from '../services/pillarService';
import { getProductsByBrand } from '../services/productService';
import FavoriteButton from '../components/FavoriteButton';
import LibraryItemCard from '../components/LibraryItemCard';
import TagSelector from '../components/TagSelector';
import TagManagerModal from '../components/TagManagerModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ImageRefinementPanel from '../components/ImageRefinementPanel';
import OneDriveConnectModal from '../components/OneDriveConnectModal';
import OneDriveBrowserModal from '../components/OneDriveBrowserModal';
import MaskDrawingCanvas from '../components/MaskDrawingCanvas';
import AdCreationModal from '../components/AdCreationModal';
import DownloadFormatDropdown from '../components/DownloadFormatDropdown';
import FolderCard from '../components/FolderCard';
import ImageCropCanvas from '../components/ImageCropCanvas';
import MoveToFolderModal from '../components/MoveToFolderModal';
import { useImageRefinementJob, startImageRefinement } from '../hooks/useImageRefinementJob';

const LIBRARY_TABS = [
  { id: 'media', label: 'Media Bank', icon: Grid },
  { id: 'ads', label: 'Ad Creatives', icon: Sparkles },
  { id: 'casting', label: 'Casting', icon: Users },
  { id: 'locations', label: 'Locations', icon: MapPin }
];

const AssetLibrary = () => {
  const { creatives, brands, selectedBrand } = useApp();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get planId filter from URL query params (e.g., /library?planId=abc123)
  const planIdFilter = searchParams.get('planId');

  // Tab and view state
  const [activeTab, setActiveTab] = useState('media');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');

  // Library items state
  const [libraryItems, setLibraryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Filtering state
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Sort state
  const [sortBy, setSortBy] = useState('createdAt');

  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [itemToTag, setItemToTag] = useState(null);

  // Refinement state
  const [showRefinementPanel, setShowRefinementPanel] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [refinementError, setRefinementError] = useState(null);
  const [refinementStatus, setRefinementStatus] = useState(null);
  const [refinementSuccess, setRefinementSuccess] = useState(false);
  const [refinementJobId, setRefinementJobId] = useState(null);
  const [refinementContext, setRefinementContext] = useState(null); // Store context for when job completes
  const [showSplitConfirm, setShowSplitConfirm] = useState(false);
  const [versionToSplit, setVersionToSplit] = useState(null);
  const [showDeleteVersionConfirm, setShowDeleteVersionConfirm] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);

  // Carousel refinement state
  const [selectedCarouselCardIndex, setSelectedCarouselCardIndex] = useState(0);
  const carouselContainerRef = useRef(null);

  // OneDrive state
  const [oneDriveConnected, setOneDriveConnected] = useState(false);
  const [showOneDriveConnect, setShowOneDriveConnect] = useState(false);
  const [showOneDriveBrowser, setShowOneDriveBrowser] = useState(false);

  // Upscale state
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [showUpscaleConfirm, setShowUpscaleConfirm] = useState(false);
  const [selectedUpscaleFactor, setSelectedUpscaleFactor] = useState('x2');
  const [upscaleError, setUpscaleError] = useState(null);
  const UPSCALE_CREDITS = 2200;

  // Background removal state
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [bgRemovalError, setBgRemovalError] = useState(null);

  // Magnifier state
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef(null);

  // Fullscreen image viewer state - stores index into history array
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [showFullscreenThumbs, setShowFullscreenThumbs] = useState(true);
  const fullscreenThumbsTimeout = useRef(null);
  const touchStartX = useRef(null);

  // Selected version prompt for refinement panel
  const [selectedVersionPrompt, setSelectedVersionPrompt] = useState('');

  // Download all versions state
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  // Inpainting state
  const [showMaskDrawer, setShowMaskDrawer] = useState(false);
  const [isInpainting, setIsInpainting] = useState(false);
  const [inpaintMode, setInpaintMode] = useState('remove'); // 'remove' or 'replace'
  const [inpaintPrompt, setInpaintPrompt] = useState('');
  const [inpaintError, setInpaintError] = useState(null);

  // Crop state
  const [showCropCanvas, setShowCropCanvas] = useState(false);

  // Asset creation state
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [showMediaUploadModal, setShowMediaUploadModal] = useState(false);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const currentFolderIdRef = useRef(null); // Ref to track current folder for stale request detection
  const [currentFolderName, setCurrentFolderName] = useState(null);
  const [folderPath, setFolderPath] = useState([]); // Breadcrumb trail for nested folders
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [showDeleteFolderConfirm, setShowDeleteFolderConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [isMovingItem, setIsMovingItem] = useState(false);
  const [isDragging, setIsDragging] = useState(false); // Track if something is being dragged

  // Multi-select mode
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  // File Upload
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentUser?.uid || !selectedBrand?.id) return;

    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const item = {
          title: file.name.replace(/\.[^/.]+$/, ""),
          name: file.name,
          type: 'image',
          imageUrl: reader.result,
          userId: currentUser.uid,
          brandId: selectedBrand.id,
          tags: [],
          isFavorite: false
        };
        await saveLibraryItem(item, currentUser.uid, selectedBrand.id);
        loadItems();
        loadTags();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const [pillars, setPillars] = useState([]);
  const [isAdCreating, setIsAdCreating] = useState(false);
  const [mediaToUse, setMediaToUse] = useState(null);
  const createMenuRef = useRef(null);

  // Get fullscreen navigation data - handle both history (single images) and carousels
  const isCarouselFullscreen = selectedItem?.type === 'carousel' && selectedItem?.carouselImages?.length > 0;
  const fullscreenHistory = isCarouselFullscreen
    ? selectedItem.carouselImages
    : (selectedItem?.history || []);
  const fullscreenImageUrl = fullscreenIndex !== null
    ? (isCarouselFullscreen
        ? (fullscreenHistory[fullscreenIndex]?.imageUrl || selectedItem?.imageUrl)
        : (fullscreenHistory[fullscreenIndex]?.imageUrl || selectedItem?.url || selectedItem?.imageUrl))
    : null;

  // Navigate fullscreen images
  const navigateFullscreen = (direction) => {
    if (fullscreenIndex === null || fullscreenHistory.length === 0) return;
    if (direction === 'prev' && fullscreenIndex > 0) {
      setFullscreenIndex(fullscreenIndex - 1);
      // Sync carousel card index when navigating carousel in fullscreen
      if (isCarouselFullscreen) {
        setSelectedCarouselCardIndex(fullscreenIndex - 1);
      }
    } else if (direction === 'next' && fullscreenIndex < fullscreenHistory.length - 1) {
      setFullscreenIndex(fullscreenIndex + 1);
      // Sync carousel card index when navigating carousel in fullscreen
      if (isCarouselFullscreen) {
        setSelectedCarouselCardIndex(fullscreenIndex + 1);
      }
    }
  };

  // Handle mouse activity in fullscreen to show/hide thumbnails
  const handleFullscreenMouseActivity = () => {
    setShowFullscreenThumbs(true);
    if (fullscreenThumbsTimeout.current) {
      clearTimeout(fullscreenThumbsTimeout.current);
    }
    fullscreenThumbsTimeout.current = setTimeout(() => {
      setShowFullscreenThumbs(false);
    }, 2500);
  };

  // Subscribe to image refinement job updates
  const { job: refinementJob, loading: refinementJobLoading, error: refinementJobError } = useImageRefinementJob(refinementJobId);

  // Debug logging for refinement job
  useEffect(() => {
    if (refinementJobId) {
      console.log('🔍 Refinement job subscription:', {
        jobId: refinementJobId,
        loading: refinementJobLoading,
        error: refinementJobError,
        job: refinementJob ? {
          status: refinementJob.status,
          progress: refinementJob.progress,
          hasResult: !!refinementJob.result
        } : null
      });
    }
  }, [refinementJobId, refinementJob, refinementJobLoading, refinementJobError]);

  // Handle refinement job completion
  useEffect(() => {
    if (!refinementJob || !refinementContext) return;

    const { status, result, error, progress } = refinementJob;
    console.log('📊 Refinement job update:', { status, progress, hasResult: !!result, error });

    // Update progress message
    if (progress?.message && status === 'processing') {
      setRefinementStatus(progress.message);
    }

    if (status === 'completed' && result?.url) {
      // Job completed successfully - update the library item
      const handleCompletion = async () => {
        try {
          const { itemToRefine, isCarousel, carouselCardIndex, prompt } = refinementContext;
          let finalImageUrl = result.url;

          // Upload if base64
          if (finalImageUrl.startsWith('data:image')) {
            finalImageUrl = await uploadLibraryImage(
              finalImageUrl,
              currentUser.uid,
              selectedBrand.id,
              itemToRefine.type || 'image'
            );
          }

          let updatedItem;
          let cardIndexToUpdate = null;
          if (isCarousel) {
            // Use the card index saved when refinement started (not current selection)
            cardIndexToUpdate = carouselCardIndex ?? 0;
            updatedItem = await updateCarouselCardImage(
              itemToRefine.id,
              cardIndexToUpdate,
              finalImageUrl,
              prompt,
              currentUser.uid
            );
          } else {
            updatedItem = await addRefinedVersion(
              itemToRefine.id,
              finalImageUrl,
              prompt,
              currentUser.uid
            );
          }

          // Update local state - set the item first, then the card index
          setSelectedItem(updatedItem);
          setLibraryItems(prev =>
            prev.map(i => i.id === itemToRefine.id ? updatedItem : i)
          );

          // For carousels, sync the selected card index to show the updated card
          if (isCarousel && cardIndexToUpdate !== null) {
            setSelectedCarouselCardIndex(cardIndexToUpdate);
          }

          // Auto-select the new refined version
          if (!isCarousel && updatedItem.history?.length > 0) {
            setSelectedVersionPrompt(prompt);
            if (fullscreenIndex !== null) {
              setFullscreenIndex(updatedItem.history.length - 1);
            }
          }

          // Show success
          setRefinementSuccess(true);
          setRefinementError(null);
          setRefinementStatus(null);
          setTimeout(() => setRefinementSuccess(false), 3000);

        } catch (err) {
          console.error(`[${new Date().toISOString()}] Failed to save refined image:`, err);
          setRefinementError(err.message || 'Failed to save refined image');
          setRefinementStatus(null);
        } finally {
          setIsRefining(false);
          setRefinementJobId(null);
          setRefinementContext(null);
        }
      };

      handleCompletion();
    } else if (status === 'failed') {
      // Job failed
      setRefinementError(error || 'Image refinement failed');
      setRefinementStatus(null);
      setIsRefining(false);
      setRefinementJobId(null);
      setRefinementContext(null);
    }
  }, [refinementJob, refinementContext, currentUser?.uid, selectedBrand?.id, fullscreenIndex]);

  // Reset thumbs visibility when entering fullscreen
  useEffect(() => {
    if (fullscreenIndex !== null) {
      setShowFullscreenThumbs(true);
      handleFullscreenMouseActivity();
    }
    return () => {
      if (fullscreenThumbsTimeout.current) {
        clearTimeout(fullscreenThumbsTimeout.current);
      }
    };
  }, [fullscreenIndex]);

  // Handle keyboard navigation for fullscreen viewer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (fullscreenIndex === null) return;

      if (e.key === 'Escape') {
        setFullscreenIndex(null);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateFullscreen('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateFullscreen('next');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenIndex, fullscreenHistory.length]);

  // Check OneDrive connection status
  const checkOneDriveStatus = useCallback(async () => {
    if (!currentUser?.uid || !selectedBrand?.id) return;
    try {
      const status = await checkOneDriveConnection(currentUser.uid, selectedBrand.id);
      setOneDriveConnected(status.connected);
    } catch (error) {
      console.error('Error checking OneDrive status:', error);
      setOneDriveConnected(false);
    }
  }, [currentUser?.uid, selectedBrand?.id]);

  useEffect(() => {
    checkOneDriveStatus();
  }, [checkOneDriveStatus]);

  // Handle OneDrive button click
  const handleOneDriveClick = () => {
    if (oneDriveConnected) {
      setShowOneDriveBrowser(true);
    } else {
      setShowOneDriveConnect(true);
    }
  };

  // Handle OneDrive connection success
  const handleOneDriveConnected = () => {
    setOneDriveConnected(true);
    setShowOneDriveConnect(false);
    // Open browser after successful connection
    setShowOneDriveBrowser(true);
  };

  // Handle OneDrive file selection
  const handleOneDriveSelect = async (files) => {
    if (!currentUser?.uid || !selectedBrand?.id || files.length === 0) return;

    try {
      // Determine item type based on active tab
      let itemType = 'image';
      if (activeTab === 'casting') itemType = 'casting';
      else if (activeTab === 'locations') itemType = 'location';

      await saveOneDriveItems(files, currentUser.uid, selectedBrand.id, itemType);
      await loadItems();
    } catch (error) {
      console.error('Error linking OneDrive files:', error);
    }
  };

  // Load folders for the current location (root or inside a folder)
  const loadFolders = useCallback(async () => {
    const expectedFolderId = currentFolderId; // Capture at call time

    // Update ref synchronously BEFORE any async work - this ensures the ref
    // is always in sync when we check for stale requests later
    currentFolderIdRef.current = expectedFolderId;

    if (!currentUser?.uid || !selectedBrand?.id) {
      return;
    }

    try {
      let typeFilter;
      if (activeTab === 'media') {
        typeFilter = 'image'; // Use single type for folder queries
      } else if (activeTab === 'ads') {
        typeFilter = 'ad_creative';
      } else if (activeTab === 'casting') {
        typeFilter = 'casting';
      } else {
        typeFilter = 'location';
      }

      // Load folders for current location (root = null, or subfolders of currentFolderId)
      const parentFolderId = currentFolderId || null;
      const loadedFolders = await getFoldersWithThumbnails(currentUser.uid, selectedBrand.id, typeFilter, parentFolderId);

      // Check if currentFolderId changed while we were loading (stale request)
      if (currentFolderIdRef.current !== expectedFolderId) {
        return;
      }

      setFolders([...loadedFolders]);
    } catch (error) {
      console.error('Failed to load folders:', error);
      if (currentFolderIdRef.current === expectedFolderId) {
        setFolders([]);
      }
    }
  }, [currentUser?.uid, selectedBrand?.id, activeTab, currentFolderId]);

  // Load library items when tab, brand, or filters change
  const loadItems = useCallback(async () => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    setIsLoading(true);
    setLoadError(null);

    try {
      let typeFilter;
      if (activeTab === 'media') {
        typeFilter = ['image', 'carousel']; // Include both images and carousels in Media Bank
      } else if (activeTab === 'ads') {
        typeFilter = 'ad_creative';
      } else if (activeTab === 'casting') {
        typeFilter = 'casting';
      } else {
        typeFilter = 'location';
      }

      let items;
      if (currentFolderId) {
        // Load items in the current folder
        items = await getItemsInFolder(currentUser.uid, selectedBrand.id, currentFolderId, {
          type: typeFilter,
          isFavorite: filterFavorites ? true : undefined,
          tags: selectedTags.length > 0 ? selectedTags : undefined,
          orderByField: sortBy
        });
      } else {
        // Load root items (items not in any folder)
        const allItems = await loadLibraryItems(currentUser.uid, selectedBrand.id, {
          type: typeFilter,
          isFavorite: filterFavorites ? true : undefined,
          tags: selectedTags.length > 0 ? selectedTags : undefined,
          orderByField: sortBy,
          planId: planIdFilter || undefined
        });
        // Filter to only show items not in a folder
        items = allItems.filter(item => !item.folderId);
      }

      // Special handling for Media Bank to exclude ads
      if (activeTab === 'media') {
        const mediaItems = items.filter(item =>
          (item.type === 'image' || item.type === 'carousel') && !item.isAdCreative
        );
        setLibraryItems(mediaItems);
      } else {
        setLibraryItems(items);
      }
    } catch (error) {
      console.error('Failed to load library items:', error);
      setLoadError('Failed to load items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid, selectedBrand?.id, activeTab, filterFavorites, selectedTags, sortBy, planIdFilter, currentFolderId]);

  // Load tags for the brand
  const loadTags = useCallback(async () => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    try {
      const tags = await getBrandTags(currentUser.uid, selectedBrand.id);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  }, [currentUser?.uid, selectedBrand?.id]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Load folders when dependencies change - ref is updated synchronously inside loadFolders
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // Load pillars for the brand
  useEffect(() => {
    const fetchPillars = async () => {
      if (!selectedBrand?.id) return;
      try {
        const brandPillars = await getPillarsByBrand(selectedBrand.id);
        setPillars(brandPillars);
      } catch (error) {
        console.error('Failed to fetch pillars:', error);
      }
    };
    fetchPillars();
  }, [selectedBrand?.id]);

  // Handle click outside for create menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset filters and folder when changing tabs
  useEffect(() => {
    setSearchQuery('');
    setSelectedTags([]);
    setFilterFavorites(false);
    setCurrentFolderId(null);
    setCurrentFolderName(null);
    setFolderPath([]);
  }, [activeTab]);

  // Load folder path when navigating into a folder
  useEffect(() => {
    const loadPath = async () => {
      if (currentFolderId && currentUser?.uid) {
        const path = await getFolderPath(currentFolderId, currentUser.uid);
        setFolderPath(path);
      } else {
        setFolderPath([]);
      }
    };
    loadPath();
  }, [currentFolderId, currentUser?.uid]);

  // Folder navigation handlers
  const handleOpenFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderName(folder.name);
  };

  const handleNavigateToRoot = () => {
    setCurrentFolderId(null);
    setCurrentFolderName(null);
    setFolderPath([]);
  };

  const handleNavigateToFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderName(folder.name);
  };

  // Drag/drop handlers
  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDropOnFolder = async (dragData, targetFolder) => {
    if (!currentUser?.uid) return;

    try {
      if (dragData.type === 'item') {
        // Move single item to folder
        await moveItemsToFolder([dragData.itemId], targetFolder.id, currentUser.uid);
      } else if (dragData.type === 'items') {
        // Move multiple selected items to folder
        await moveItemsToFolder(dragData.itemIds, targetFolder.id, currentUser.uid);
        // Clear selection after successful move
        setSelectedItemIds(new Set());
        setIsSelectionMode(false);
      } else if (dragData.type === 'folder') {
        // Move folder to folder (make it a subfolder)
        if (dragData.folderId !== targetFolder.id) {
          await moveFolderToFolder(dragData.folderId, targetFolder.id, currentUser.uid);
        }
      }
      // Refresh both folders and items
      loadFolders();
      loadItems();
    } catch (error) {
      console.error('Failed to move item/folder:', error);
    }
  };

  // Handle folder rename (inline)
  const handleFolderRename = async (folder, newName) => {
    if (!currentUser?.uid || !newName.trim()) return;

    try {
      await updateFolder(folder.id, { name: newName.trim() }, currentUser.uid);
      loadFolders();
      // Update folder path if this folder is in the path
      if (folderPath.some(f => f.id === folder.id)) {
        setFolderPath(prev => prev.map(f => f.id === folder.id ? { ...f, name: newName.trim() } : f));
      }
      // Update current folder name if it's the current folder
      if (currentFolderId === folder.id) {
        setCurrentFolderName(newName.trim());
      }
    } catch (error) {
      console.error('Failed to rename folder:', error);
    }
  };

  // Create folder handler
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !currentUser?.uid || !selectedBrand?.id) return;

    setIsCreatingFolder(true);
    try {
      let itemType;
      if (activeTab === 'media') {
        itemType = 'image';
      } else if (activeTab === 'ads') {
        itemType = 'ad_creative';
      } else if (activeTab === 'casting') {
        itemType = 'casting';
      } else {
        itemType = 'location';
      }

      // Pass parentFolderId to create subfolder if inside a folder
      await createFolder({ name: newFolderName.trim(), parentFolderId: currentFolderId }, currentUser.uid, selectedBrand.id, itemType);
      setNewFolderName('');
      setShowCreateFolderModal(false);
      loadFolders();
    } catch (error) {
      console.error('Failed to create folder:', error);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // Rename folder handler
  const handleRenameFolder = async () => {
    if (!folderToRename || !newFolderName.trim() || !currentUser?.uid) return;

    try {
      await updateFolder(folderToRename.id, { name: newFolderName.trim() }, currentUser.uid);
      setNewFolderName('');
      setFolderToRename(null);
      setShowRenameFolderModal(false);
      loadFolders();
    } catch (error) {
      console.error('Failed to rename folder:', error);
    }
  };

  // Delete folder handler
  const handleConfirmDeleteFolder = async () => {
    if (!folderToDelete || !currentUser?.uid) return;

    try {
      await deleteFolder(folderToDelete.id, currentUser.uid);
      setFolderToDelete(null);
      setShowDeleteFolderConfirm(false);
      loadFolders();
      loadItems(); // Reload items as they may have been moved to root
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  // Download folder as ZIP handler
  const [isDownloadingFolder, setIsDownloadingFolder] = useState(false);
  const handleDownloadFolder = async (folder) => {
    if (!currentUser?.uid || !selectedBrand?.id || isDownloadingFolder) return;

    setIsDownloadingFolder(true);
    try {
      const { folderName, items } = await getAllItemsInFolderRecursive(
        folder.id,
        currentUser.uid,
        selectedBrand.id
      );

      if (items.length === 0) {
        console.warn('No items to download in folder');
        return;
      }

      const zip = new JSZip();

      // Fetch and add each image to the ZIP
      const fetchPromises = items.map(async (item, index) => {
        try {
          const response = await fetch(item.downloadUrl);
          if (!response.ok) throw new Error('Failed to fetch');

          const blob = await response.blob();

          // Determine file extension from URL or content type
          let extension = 'png';
          const urlParts = item.downloadUrl.split('?')[0].split('.');
          if (urlParts.length > 1) {
            const urlExt = urlParts[urlParts.length - 1].toLowerCase();
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(urlExt)) {
              extension = urlExt;
            }
          }

          // Build filename with folder path
          const itemName = item.title || item.name || `image_${index + 1}`;
          const safeName = itemName.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
          const filePath = item.folderPath
            ? `${item.folderPath}/${safeName}.${extension}`
            : `${safeName}.${extension}`;

          zip.file(filePath, blob);
        } catch (error) {
          console.error(`Failed to fetch image ${item.id}:`, error);
        }
      });

      await Promise.all(fetchPromises);

      // Generate and download the ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${folderName.replace(/[^a-z0-9]/gi, '_')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download folder:', error);
    } finally {
      setIsDownloadingFolder(false);
    }
  };

  // Move item to folder handler (supports both single item and bulk selection)
  const handleMoveToFolder = async (targetFolderId) => {
    if (!currentUser?.uid) return;

    // Determine which items to move: bulk selection or single item
    const itemIdsToMove = selectedItemIds.size > 0
      ? Array.from(selectedItemIds)
      : itemToMove ? [itemToMove.id] : [];

    if (itemIdsToMove.length === 0) return;

    setIsMovingItem(true);
    try {
      await moveItemsToFolder(itemIdsToMove, targetFolderId, currentUser.uid);
      setItemToMove(null);
      setSelectedItemIds(new Set());
      setIsSelectionMode(false);
      setShowMoveToFolderModal(false);
      loadItems();
      loadFolders(); // Refresh folder thumbnails
    } catch (error) {
      console.error('Failed to move item(s):', error);
    } finally {
      setIsMovingItem(false);
    }
  };

  // Remove item from current folder (move to root)
  const handleRemoveFromFolder = async (item) => {
    if (!currentUser?.uid) return;

    try {
      await moveItemsToFolder([item.id], null, currentUser.uid);
      loadItems();
      loadFolders();
    } catch (error) {
      console.error('Failed to remove item from folder:', error);
    }
  };

  // Multi-select handlers
  const handleToggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItemIds(new Set());
  };

  const handleToggleItemSelection = (item) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(item.id)) {
        newSet.delete(item.id);
      } else {
        newSet.add(item.id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = new Set(filteredLibraryItems.map(item => item.id));
    setSelectedItemIds(allIds);
  };

  const handleClearSelection = () => {
    setSelectedItemIds(new Set());
  };

  const handleBulkMoveToFolder = async (targetFolderId) => {
    if (selectedItemIds.size === 0 || !currentUser?.uid) return;

    setIsMovingItem(true);
    try {
      await moveItemsToFolder(Array.from(selectedItemIds), targetFolderId, currentUser.uid);
      setSelectedItemIds(new Set());
      setIsSelectionMode(false);
      setShowMoveToFolderModal(false);
      loadItems();
      loadFolders();
    } catch (error) {
      console.error('Failed to move items:', error);
    } finally {
      setIsMovingItem(false);
    }
  };

  // Filter creatives (from context) based on search and brand
  const filteredCreatives = creatives.filter(creative => {
    const matchesSearch = creative.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      creative.brandName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBrand = selectedBrand ? creative.brandId === selectedBrand.id : true;
    return matchesSearch && matchesBrand;
  });

  // Filter library items based on search
  const filteredLibraryItems = libraryItems.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (item.title || '').toLowerCase().includes(searchLower) ||
      (item.name || '').toLowerCase().includes(searchLower) ||
      (item.description || '').toLowerCase().includes(searchLower)
    );
  });

  // Handle favorite toggle
  const handleFavoriteToggle = async (item) => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    let itemToToggle = item;

    // Handle context creatives (unsaved items)
    if (item.isContextCreative) {
      try {
        console.log('Auto-saving context creative for favoriting...');
        const itemToSave = {
          title: item.title || item.name || 'Untitled Creative',
          description: item.description || '',
          imageUrl: item.imageUrl || item.url,
          type: item.type || 'image',
          tags: item.tags || [],
          isFavorite: false // Will be toggled below
        };

        const savedItemId = await saveLibraryItem(itemToSave, currentUser.uid, selectedBrand.id);

        itemToToggle = {
          ...item,
          id: savedItemId,
          isContextCreative: false
        };

        // Update library items list to include the newly saved item
        setLibraryItems(prev => [itemToToggle, ...prev.filter(i => i.id !== item.id)]);

        // Update selectedItem if it was the one being toggled
        if (selectedItem?.id === item.id) {
          setSelectedItem(itemToToggle);
        }
      } catch (error) {
        console.error('Failed to auto-save context creative for favorite:', error);
        return;
      }
    }

    try {
      const newStatus = await toggleFavorite(itemToToggle.id, currentUser.uid);

      // Update local items list
      setLibraryItems(prev =>
        prev.map(i => i.id === itemToToggle.id ? { ...i, isFavorite: newStatus } : i)
      );

      // Also update selectedItem if it's the one being toggled
      if (selectedItem && (selectedItem.id === itemToToggle.id || selectedItem.id === item.id)) {
        setSelectedItem(prev => ({ ...prev, isFavorite: newStatus }));
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  // Handle delete
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete || !currentUser?.uid) return;

    try {
      await deleteLibraryItem(itemToDelete.id, currentUser.uid);
      setLibraryItems(prev => prev.filter(i => i.id !== itemToDelete.id));
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  // Handle tag rename
  const handleRenameTag = async (oldName, newName) => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    await renameTag(oldName, newName, currentUser.uid, selectedBrand.id);
    await loadTags();
    await loadItems();
  };

  // Handle tag delete
  const handleDeleteTag = async (tagName) => {
    if (!currentUser?.uid || !selectedBrand?.id) return;

    await deleteTagFromAll(tagName, currentUser.uid, selectedBrand.id);
    await loadTags();
    await loadItems();
  };

  // Handle tag filter click from card
  const handleTagFilterClick = (tagName) => {
    if (!selectedTags.includes(tagName)) {
      setSelectedTags([...selectedTags, tagName]);
    }
  };

  // Handle item tag update
  const handleUpdateItemTags = async (newTags) => {
    if (!itemToTag || !currentUser?.uid) return;

    try {
      await updateItemTags(itemToTag.id, newTags, currentUser.uid);
      setLibraryItems(prev =>
        prev.map(i => i.id === itemToTag.id ? { ...i, tags: newTags } : i)
      );
      await loadTags(); // Refresh available tags
    } catch (error) {
      console.error('Failed to update tags:', error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterFavorites(false);
    setSelectedTags([]);
    // Clear planId from URL
    if (planIdFilter) {
      setSearchParams({});
    }
  };

  // Clear just the planId filter
  const clearPlanIdFilter = () => {
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || filterFavorites || selectedTags.length > 0 || planIdFilter;

  // Handle image refinement
  const handleRefineImage = async (prompt, userReferenceImages = []) => {
    console.log('handleRefineImage called with:', {
      selectedItem: selectedItem?.id,
      type: selectedItem?.type,
      hasImageUrl: !!selectedItem?.imageUrl,
      prompt: prompt?.substring(0, 50),
      isContextCreative: selectedItem?.isContextCreative
    });

    if (!selectedItem || !prompt?.trim() || !currentUser?.uid || !selectedBrand?.id) {
      console.log('Early return - missing required values:', {
        hasSelectedItem: !!selectedItem,
        hasPrompt: !!prompt?.trim(),
        hasUser: !!currentUser?.uid,
        hasBrand: !!selectedBrand?.id
      });
      return;
    }

    // Determine the image URL to refine (handle carousels)
    const isCarousel = selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0;
    const imageToRefine = isCarousel
      ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
      : selectedItem.imageUrl;

    console.log(`[${new Date().toISOString()}] Refinement config:`, { isCarousel, imageToRefine: imageToRefine?.substring(0, 50) });

    if (!imageToRefine) {
      setRefinementError('No image available to refine');
      return;
    }

    // Context creatives need to be saved to the library first - auto-save them
    let itemToRefine = selectedItem;
    if (selectedItem.isContextCreative) {
      console.log(`[${new Date().toISOString()}] Auto-saving context creative before refinement...`);
      try {
        // Prepare item data for saving
        const itemToSave = {
          title: selectedItem.title || selectedItem.name || 'Untitled Creative',
          description: selectedItem.description || '',
          imageUrl: selectedItem.imageUrl,
          type: selectedItem.type || 'image',
          tags: selectedItem.tags || [],
          isFavorite: selectedItem.isFavorite || false
        };

        // Save to library
        const savedItemId = await saveLibraryItem(itemToSave, currentUser.uid, selectedBrand.id);
        console.log(`[${new Date().toISOString()}] Context creative auto-saved with ID:`, savedItemId);

        // Create updated item with real Firestore ID
        itemToRefine = {
          ...selectedItem,
          id: savedItemId,
          isContextCreative: false
        };

        // Update local state with the saved item
        setSelectedItem(itemToRefine);

        // Update library items list
        setLibraryItems(prev => [itemToRefine, ...prev.filter(i => i.id !== selectedItem.id)]);
      } catch (saveError) {
        console.error(`[${new Date().toISOString()}] Failed to auto-save context creative:`, saveError);
        setRefinementError('Failed to save creative to library. Please try again.');
        return;
      }
    }

    // Filter to only valid reference images
    const validRefImages = userReferenceImages.filter(img => img.status === 'valid' && img.url);

    setIsRefining(true);
    setRefinementError(null);
    setRefinementStatus(null);
    setRefinementSuccess(false);

    try {
      // Build reference images array for API
      // Consistently label the base image as [ORIGINAL_IMAGE] to help the AI distinguish it from style references
      const referenceImages = [{
        url: imageToRefine,
        instruction: `[ORIGINAL_IMAGE]`
      }];

      // Look up original product photos from the library item's metadata
      const productName = itemToRefine.metadata?.productName || itemToRefine.metadata?.product;
      if (productName && selectedBrand?.id) {
        try {
          const brandProducts = await getProductsByBrand(selectedBrand.id);
          const product = brandProducts.find(p =>
            p.name === productName || p.id === itemToRefine.metadata?.productId
          );
          if (product?.images?.length > 0) {
            product.images.slice(0, 3).forEach((img, idx) => {
              const imgUrl = img.url || img;
              if (imgUrl) {
                referenceImages.push({
                  url: imgUrl,
                  instruction: `[PRODUCT PHOTO ${idx + 1}] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                });
              }
            });
          } else if (product?.imageUrl) {
            referenceImages.push({
              url: product.imageUrl,
              instruction: `[PRODUCT PHOTO] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
            });
          }
        } catch (err) {
          console.warn('Could not load product photos for refinement:', err.message);
        }
      }

      // Add brand logo
      const logoUrl = selectedBrand?.logoUrl || selectedBrand?.logo || selectedBrand?.branding?.logoUrl;
      if (logoUrl) {
        referenceImages.push({
          url: logoUrl,
          instruction: `[BRAND LOGO] - HIGH-FIDELITY OBJECT. Reproduce this logo EXACTLY as-is. Do not recolor, distort, or modify it in any way.`
        });
      }

      // Add user's labeled reference images
      validRefImages.forEach((refImg) => {
        referenceImages.push({
          url: refImg.url,
          instruction: `[REFERENCE: ${refImg.label}]`
        });
      });

      // Build simple refinement prompt with image context
      const refinementPrompt = validRefImages.length > 0
        ? `Edit [ORIGINAL_IMAGE]. Reference images: ${validRefImages.map(img => img.label).join(', ')}.\n\n${prompt.trim()}`
        : `Edit [ORIGINAL_IMAGE].\n\n${prompt.trim()}`;

      // Start background job for image refinement
      // This returns immediately - job completion handled by useImageRefinementJob hook
      console.log(`[${new Date().toISOString()}] Starting background refinement job...`);
      const { jobId } = await startImageRefinement({
        userId: currentUser.uid,
        brandId: selectedBrand.id,
        originalImageUrl: imageToRefine,
        refinementPrompt,
        referenceImages,
        libraryItemId: itemToRefine.id
      });

      console.log(`[${new Date().toISOString()}] Refinement job started:`, jobId);

      // Store context for when job completes (including carousel card index)
      setRefinementJobId(jobId);
      setRefinementContext({
        itemToRefine,
        isCarousel,
        carouselCardIndex: isCarousel ? selectedCarouselCardIndex : null,
        prompt: prompt.trim()
      });

      // Show processing message
      setRefinementStatus('Generating refined image...');

    } catch (error) {
      console.error(`[${new Date().toISOString()}] Failed to start refinement job:`, error);
      setRefinementError(error.message || 'Failed to start image refinement');
      setIsRefining(false);
    }
  };

  // Handle upscaling an image
  const handleUpscaleImage = async () => {
    if (!selectedItem || !currentUser?.uid) return;

    // Determine the image URL to upscale (handle carousels)
    const isCarousel = selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0;
    const imageToUpscale = isCarousel
      ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
      : selectedItem.imageUrl;

    if (!imageToUpscale) {
      setUpscaleError('No image available to upscale');
      return;
    }

    setIsUpscaling(true);
    setUpscaleError(null);
    setShowUpscaleConfirm(false);

    try {
      console.log('Upscaling image:', { factor: selectedUpscaleFactor, imageUrl: imageToUpscale?.substring(0, 50) });

      // Call the upscale API with retry logic for overloaded model
      let response;
      let retries = 0;
      const MAX_RETRIES = 1;

      while (retries <= MAX_RETRIES) {
        response = await fetch('http://localhost:3001/api/upscale-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: imageToUpscale,
            upscaleFactor: selectedUpscaleFactor,
            userId: currentUser.uid
          })
        });

        if (response.ok) break;

        const errorData = await response.json().catch(() => ({}));
        const isOverloaded = response.status === 500 && (
          errorData.details?.toLowerCase().includes('overloaded') ||
          errorData.error?.toLowerCase().includes('overloaded')
        );

        if (isOverloaded && retries < MAX_RETRIES) {
          retries++;
          console.log(`⚠️ Model overloaded. Retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
          setUpscaleError(`Model is overloaded. Automatic retry in 60 seconds...`);
          await new Promise(resolve => setTimeout(resolve, 60000));
          setUpscaleError(null);
          continue;
        }

        console.error('Upscale error:', response.status, errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to upscale image');
      }

      const data = await response.json();
      console.log('Upscale response:', data);

      if (!data.upscaledImageUrl) {
        throw new Error('No upscaled image URL in response');
      }

      // Save the upscaled version to history
      const upscaleMetadata = {
        factor: selectedUpscaleFactor,
        originalDimensions: data.originalDimensions,
        upscaledDimensions: data.upscaledDimensions
      };

      let updatedItem;
      if (isCarousel) {
        updatedItem = await addUpscaledCarouselCard(
          selectedItem.id,
          selectedCarouselCardIndex,
          data.upscaledImageUrl,
          upscaleMetadata,
          currentUser.uid
        );
      } else {
        updatedItem = await addUpscaledVersion(
          selectedItem.id,
          data.upscaledImageUrl,
          upscaleMetadata,
          currentUser.uid
        );
      }

      // Update local state
      setSelectedItem(updatedItem);
      setLibraryItems(prev =>
        prev.map(i => i.id === selectedItem.id ? updatedItem : i)
      );

      console.log('Upscale complete:', {
        factor: selectedUpscaleFactor,
        from: `${data.originalDimensions.width}x${data.originalDimensions.height}`,
        to: `${data.upscaledDimensions.width}x${data.upscaledDimensions.height}`,
        creditsUsed: data.creditsUsed
      });

    } catch (error) {
      console.error('Upscale failed:', error);
      setUpscaleError(error.message || 'Failed to upscale image');
    } finally {
      setIsUpscaling(false);
    }
  };

  // Handle background removal
  const handleRemoveBackground = async () => {
    if (!selectedItem || !currentUser?.uid || !selectedBrand?.id) return;

    // Determine the image URL (handle carousels)
    const isCarousel = selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0;
    const imageToProcess = isCarousel
      ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
      : selectedItem.imageUrl;

    if (!imageToProcess) {
      setBgRemovalError('No image available to process');
      return;
    }

    setIsRemovingBackground(true);
    setBgRemovalError(null);

    try {
      console.log('Removing background from image:', imageToProcess?.substring(0, 50));

      const response = await fetch('http://localhost:3001/api/remove-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: imageToProcess,
          userId: currentUser.uid
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Provide user-friendly error messages based on status code
        let errorMessage = errorData.details || errorData.error || 'Background removal failed';

        if (response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please wait a minute and try again.';
        } else if (response.status === 402) {
          errorMessage = errorData.message || 'Insufficient credits for this operation.';
        } else if (response.status === 403) {
          errorMessage = errorData.message || 'Active subscription required.';
        } else if (response.status === 500) {
          // Check for specific error types in the details
          if (errorData.details?.includes('overloaded') || errorData.details?.includes('RESOURCE_EXHAUSTED')) {
            errorMessage = 'AI service is currently busy. Please try again in a moment.';
          } else if (errorData.details?.includes('did not return an image')) {
            errorMessage = 'Failed to process image. Please try a different image.';
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Background removal complete:', data);

      if (!data.url) {
        throw new Error('No image URL in response');
      }

      // Add as a new version in the library item's history
      if (isCarousel) {
        // For carousels, update the specific card
        const updatedCarouselImages = [...selectedItem.carouselImages];
        const currentCard = updatedCarouselImages[selectedCarouselCardIndex];

        // Initialize history if needed
        if (!currentCard.history) {
          currentCard.history = [{
            imageUrl: currentCard.imageUrl,
            isOriginal: true,
            createdAt: selectedItem.createdAt || new Date().toISOString()
          }];
        }

        // Add new version
        currentCard.history.push({
          imageUrl: data.url,
          prompt: 'Background removed',
          refinedAt: new Date().toISOString(),
          isOriginal: false,
          hasTransparency: true
        });
        currentCard.imageUrl = data.url;
        updatedCarouselImages[selectedCarouselCardIndex] = currentCard;

        // Update in Firestore
        await updateLibraryItem(selectedItem.id, currentUser.uid, selectedBrand.id, {
          carouselImages: updatedCarouselImages
        });

        // Update local state
        setSelectedItem(prev => ({
          ...prev,
          carouselImages: updatedCarouselImages
        }));
        setLibraryItems(prev => prev.map(item =>
          item.id === selectedItem.id ? { ...item, carouselImages: updatedCarouselImages } : item
        ));
      } else {
        // For single images, use addRefinedVersion
        // Signature: addRefinedVersion(itemId, newImageUrl, prompt, userId)
        await addRefinedVersion(
          selectedItem.id,
          data.url,
          'Background removed',
          currentUser.uid
        );

        // Update local state with the new version
        const newHistory = selectedItem.history ? [...selectedItem.history] : [{
          imageUrl: selectedItem.imageUrl,
          isOriginal: true,
          createdAt: selectedItem.createdAt || new Date().toISOString()
        }];
        newHistory.push({
          imageUrl: data.url,
          prompt: 'Background removed',
          refinedAt: new Date().toISOString(),
          isOriginal: false,
          hasTransparency: true
        });

        setSelectedItem(prev => ({
          ...prev,
          imageUrl: data.url,
          history: newHistory
        }));
        setLibraryItems(prev => prev.map(item =>
          item.id === selectedItem.id ? { ...item, imageUrl: data.url, history: newHistory } : item
        ));
      }

      console.log('Background removed successfully');

    } catch (error) {
      console.error('Background removal failed:', error);
      setBgRemovalError(error.message || 'Failed to remove background');
    } finally {
      setIsRemovingBackground(false);
    }
  };

  // Handle downloading all versions as a zip file
  const handleDownloadAllVersions = async () => {
    if (!selectedItem?.history || selectedItem.history.length === 0) return;

    setIsDownloadingAll(true);

    try {
      const zip = new JSZip();
      const title = (selectedItem.title || selectedItem.name || 'image').replace(/[^a-zA-Z0-9]/g, '_');

      // Fetch all images and add to zip
      const fetchPromises = selectedItem.history.map(async (version, index) => {
        try {
          const response = await fetch(version.imageUrl);
          if (!response.ok) throw new Error(`Failed to fetch image ${index + 1}`);

          const blob = await response.blob();
          const extension = blob.type.includes('png') ? 'png' : 'jpg';

          // Create filename with version info
          let filename;
          if (version.isOriginal) {
            filename = `${title}_v${index + 1}_original.${extension}`;
          } else if (version.isUpscaled) {
            filename = `${title}_v${index + 1}_upscaled_${version.upscaleMetadata?.factor || 'HD'}.${extension}`;
          } else {
            const promptSnippet = version.prompt?.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_') || 'refined';
            filename = `${title}_v${index + 1}_${promptSnippet}.${extension}`;
          }

          zip.file(filename, blob);
        } catch (err) {
          console.error(`Failed to fetch version ${index + 1}:`, err);
        }
      });

      await Promise.all(fetchPromises);

      // Generate and download the zip
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}_all_versions.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download all failed:', error);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  // Handle inpainting with mask
  const handleInpaint = async (maskBase64, customPrompt, customMode) => {
    if (!selectedItem || !currentUser?.uid) return;

    // Use values passed from the mask drawer
    const finalPrompt = customPrompt || inpaintPrompt;
    const finalMode = customMode || inpaintMode;

    // Handle carousel cards - use the selected card's image
    const isCarousel = selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0;
    const imageToEdit = isCarousel
      ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
      : selectedItem.imageUrl;

    if (!imageToEdit) {
      setInpaintError('No image available to edit');
      return;
    }

    // Save the card index at the start (in case it changes during async operation)
    const cardIndexToUpdate = isCarousel ? selectedCarouselCardIndex : null;

    setShowMaskDrawer(false);
    setIsInpainting(true);
    setInpaintError(null);

    try {
      // Call the inpaint API with retry logic for overloaded model
      let response;
      let retries = 0;
      const MAX_RETRIES = 1;

      while (retries <= MAX_RETRIES) {
        response = await fetch('http://localhost:3001/api/inpaint-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: imageToEdit,
            maskBase64,
            prompt: finalPrompt || (finalMode === 'remove' ? 'Remove the object in the masked area and replace with the surrounding background' : 'Edit the masked area'),
            editMode: finalMode,
            userId: currentUser.uid
          })
        });

        if (response.ok) break;

        const errorData = await response.json().catch(() => ({}));
        const isOverloaded = response.status === 500 && (
          errorData.details?.toLowerCase().includes('overloaded') ||
          errorData.error?.toLowerCase().includes('overloaded')
        );

        if (isOverloaded && retries < MAX_RETRIES) {
          retries++;
          console.log(`⚠️ Model overloaded. Retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
          setInpaintError(`Model is overloaded. Automatic retry in 60 seconds...`);
          await new Promise(resolve => setTimeout(resolve, 60000));
          setInpaintError(null);
          continue;
        }

        console.error('Inpaint error:', response.status, errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to edit image');
      }

      const data = await response.json();
      console.log('Inpaint response:', data);

      if (!data.editedImageUrl) {
        throw new Error('No edited image URL in response');
      }

      // Add the edited version to history - handle carousel cards differently
      let updatedItem;
      const editPrompt = `[${finalMode === 'remove' ? 'Removed' : 'Edited'}] ${finalPrompt || 'Masked area'}`;

      if (isCarousel && cardIndexToUpdate !== null) {
        updatedItem = await updateCarouselCardImage(
          selectedItem.id,
          cardIndexToUpdate,
          data.editedImageUrl,
          editPrompt,
          currentUser.uid
        );
        // Ensure the correct card is selected
        setSelectedCarouselCardIndex(cardIndexToUpdate);
      } else {
        updatedItem = await addRefinedVersion(
          selectedItem.id,
          data.editedImageUrl,
          editPrompt,
          currentUser.uid
        );
      }

      // Update local state
      setSelectedItem(updatedItem);
      setLibraryItems(prev =>
        prev.map(i => i.id === selectedItem.id ? updatedItem : i)
      );

      // Reset inpaint state
      setInpaintPrompt('');
      setRefinementSuccess(true);
      setTimeout(() => setRefinementSuccess(false), 3000);

    } catch (error) {
      console.error('Inpainting failed:', error);
      setInpaintError(error.message || 'Failed to edit image');
    } finally {
      setIsInpainting(false);
    }
  };

  // Handle crop completion - save cropped image as a new version
  const handleCropComplete = async (croppedDataUrl) => {
    if (!selectedItem || !currentUser?.uid) return;

    setShowCropCanvas(false);

    try {
      // Add the cropped version to history
      const updatedItem = await addRefinedVersion(
        selectedItem.id,
        croppedDataUrl,
        '[Cropped]',
        currentUser.uid
      );

      // Update local state
      setSelectedItem(updatedItem);
      setLibraryItems(prev =>
        prev.map(i => i.id === selectedItem.id ? updatedItem : i)
      );

      setRefinementSuccess(true);
      setTimeout(() => setRefinementSuccess(false), 3000);

    } catch (error) {
      console.error('Crop failed:', error);
      setRefinementError(error.message || 'Failed to save cropped image');
    }
  };

  // Handle magnifier mouse movement
  const handleMagnifierMove = (e) => {
    if (!imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMagnifierPos({ x, y });
  };

  // Handle setting active version from history
  const handleSetActiveVersion = async (historyIndex) => {
    if (!selectedItem || !currentUser?.uid) return;

    try {
      const updatedItem = await setActiveVersion(selectedItem.id, historyIndex, currentUser.uid);
      setSelectedItem(updatedItem);
      setLibraryItems(prev =>
        prev.map(i => i.id === selectedItem.id ? updatedItem : i)
      );

      // Restore the refinement prompt from the selected version for the refinement panel
      const versionPrompt = updatedItem.history?.[historyIndex]?.prompt || '';
      console.log('[AssetLibrary] Selected version prompt:', versionPrompt);
      setSelectedVersionPrompt(versionPrompt);
    } catch (error) {
      console.error('Failed to set active version:', error);
    }
  };

  // Handle split confirmation
  const handleSplitClick = (historyIndex) => {
    setVersionToSplit(historyIndex);
    setShowSplitConfirm(true);
  };

  // Handle confirmed split
  const handleConfirmSplit = async () => {
    if (versionToSplit === null || !selectedItem || !currentUser?.uid || !selectedBrand?.id) return;

    try {
      const newItemId = await splitHistoryVersion(
        selectedItem.id,
        versionToSplit,
        currentUser.uid,
        selectedBrand.id
      );

      // Refresh items to show the new split item
      await loadItems();
      setShowSplitConfirm(false);
      setVersionToSplit(null);
    } catch (error) {
      console.error('Failed to split version:', error);
    }
  };

  // Handle delete version click
  const handleDeleteVersionClick = (historyIndex) => {
    setVersionToDelete(historyIndex);
    setShowDeleteVersionConfirm(true);
  };

  // Handle confirmed delete version
  const handleConfirmDeleteVersion = async () => {
    if (versionToDelete === null || !selectedItem || !currentUser?.uid) return;

    try {
      const updatedItem = await deleteHistoryVersion(
        selectedItem.id,
        versionToDelete,
        currentUser.uid
      );

      // Update local state
      setSelectedItem(updatedItem);
      setLibraryItems(prev =>
        prev.map(i => i.id === selectedItem.id ? updatedItem : i)
      );
      setShowDeleteVersionConfirm(false);
      setVersionToDelete(null);
    } catch (error) {
      console.error('Failed to delete version:', error);
      setRefinementError(error.message || 'Failed to delete version');
      setShowDeleteVersionConfirm(false);
      setVersionToDelete(null);
    }
  };

  // Reset refinement state when closing modal
  const handleCloseDetailModal = () => {
    setSelectedItem(null);
    setShowRefinementPanel(false);
    setRefinementError(null);
    setRefinementSuccess(false);
    setIsEditing(false);
    setEditForm({});
    setSelectedCarouselCardIndex(0);
    setSelectedVersionPrompt('');
    setFullscreenIndex(null);
  };

  // Start editing - populate form with current values
  const handleStartEdit = () => {
    setEditForm({
      title: selectedItem.title || selectedItem.name || '',
      description: selectedItem.description || '',
      caption: selectedItem.caption || '',
      hashtags: selectedItem.hashtags || ''
    });
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  // Save changes
  const handleSaveEdit = async () => {
    if (!selectedItem || !currentUser?.uid) return;

    setIsSaving(true);
    try {
      // Build updates object with only changed fields
      const updates = {};
      if (editForm.title !== (selectedItem.title || selectedItem.name || '')) {
        updates.title = editForm.title;
      }
      if (editForm.description !== (selectedItem.description || '')) {
        updates.description = editForm.description;
      }
      if (selectedItem.type === 'carousel') {
        if (editForm.caption !== (selectedItem.caption || '')) {
          updates.caption = editForm.caption;
        }
        if (editForm.hashtags !== (selectedItem.hashtags || '')) {
          updates.hashtags = editForm.hashtags;
        }
      }

      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        await updateLibraryItem(selectedItem.id, updates, currentUser.uid);

        // Update local state
        const updatedItem = { ...selectedItem, ...updates };
        setSelectedItem(updatedItem);
        setLibraryItems(prev =>
          prev.map(i => i.id === selectedItem.id ? updatedItem : i)
        );
      }

      setIsEditing(false);
      setEditForm({});
    } catch (error) {
      console.error('Failed to save changes:', error);
      setRefinementError(error.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Generate AI title and description for the selected image
  const handleGenerateMetadata = async () => {
    if (!selectedItem?.imageUrl || !selectedBrand) {
      setRefinementError('Image and brand are required to generate metadata');
      return;
    }

    setIsGeneratingMetadata(true);
    setRefinementError(null);

    try {
      // Call the metadata generation API with retry logic for overloaded model
      let response;
      let retries = 0;
      const MAX_RETRIES = 1;

      while (retries <= MAX_RETRIES) {
        response = await fetch('http://localhost:3001/api/library/generate-metadata', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: selectedItem.imageUrl,
            brand: selectedBrand
          })
        });

        if (response.ok) break;

        const errorData = await response.json().catch(() => ({}));
        const isOverloaded = response.status === 500 && (
          errorData.details?.toLowerCase().includes('overloaded') ||
          errorData.error?.toLowerCase().includes('overloaded')
        );

        if (isOverloaded && retries < MAX_RETRIES) {
          retries++;
          console.log(`⚠️ Model overloaded. Retrying in 60 seconds (Attempt ${retries}/${MAX_RETRIES})...`);
          setRefinementError(`Model is overloaded. Automatic retry in 60 seconds...`);
          await new Promise(resolve => setTimeout(resolve, 60000));
          setRefinementError(null);
          continue;
        }

        console.error('Metadata generation error:', response.status, errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to generate metadata');
      }

      const data = await response.json();

      // Update the edit form with AI-generated content
      setEditForm(prev => ({
        ...prev,
        title: data.title || prev.title,
        description: data.description || prev.description
      }));

    } catch (error) {
      console.error('Failed to generate metadata:', error);
      setRefinementError(error.message || 'Failed to generate AI metadata');
    } finally {
      setIsGeneratingMetadata(false);
    }
  };

  const handleDownload = async (url, filename, e) => {
    if (e) e.stopPropagation();

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(url, '_blank');
    }
  };

  // Render items based on active tab
  const renderItems = () => {
    // Render casting/locations/creatives from libraryItems
    if (isLoading) {
      return (
        <div className="empty-state">
          <div style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-tertiary)' }}>
            Loading...
          </div>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="empty-state">
          <div style={{ color: 'var(--error-600)' }}>{loadError}</div>
          <button className="btn btn-secondary" onClick={loadItems} style={{ marginTop: 'var(--space-4)' }}>
            Try Again
          </button>
        </div>
      );
    }

    // For creatives tab, combine library items with old context creatives
    let itemsToRender = filteredLibraryItems;
    if (activeTab === 'creatives') {
      // Transform old context creatives to match LibraryItemCard expected format
      const transformedContextCreatives = filteredCreatives.map(creative => ({
        id: `context-${creative.id}`,
        name: creative.title || 'Untitled Creative',
        title: creative.title || 'Untitled Creative',
        description: creative.brandName || '',
        imageUrl: creative.imageUrl || creative.url || '',
        type: 'image',
        tags: creative.tags || [],
        isFavorite: creative.isFavorite || false,
        createdAt: creative.createdAt,
        isContextCreative: true // Flag to identify old creatives
      }));

      // Combine both sources, library items first (newer), then context creatives
      itemsToRender = [...filteredLibraryItems, ...transformedContextCreatives];
    }

    const hasNoContent = itemsToRender.length === 0 && folders.length === 0;

    if (hasNoContent) {
      const TabIcon = LIBRARY_TABS.find(t => t.id === activeTab)?.icon || FolderOpen;
      return (
        <>
          {/* Breadcrumb navigation - supports nested folders */}
          {currentFolderId && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-md)',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleNavigateToRoot}
                className="btn btn-ghost btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-2)' }}
              >
                <Home size={14} />
                Root
              </button>
              {folderPath.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <ChevronRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                  {index === folderPath.length - 1 ? (
                    <span style={{ fontWeight: 500 }}>{folder.name}</span>
                  ) : (
                    <button
                      onClick={() => handleNavigateToFolder(folder)}
                      className="btn btn-ghost btn-sm"
                      style={{ padding: 'var(--space-1) var(--space-2)' }}
                    >
                      {folder.name}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="empty-state">
            <TabIcon size={64} className="empty-state-icon" />
            <h3 className="empty-state-title">{currentFolderId ? 'Folder is empty' : `No ${activeTab} found`}</h3>
            <p className="empty-state-description">
              {hasActiveFilters
                ? 'Try adjusting your filters or search'
                : currentFolderId
                  ? 'Drag items here or create subfolders'
                  : `Generate ${activeTab} in the Creative Chat to see them here`}
            </p>
            {hasActiveFilters && (
              <button className="btn btn-secondary" onClick={clearFilters} style={{ marginTop: 'var(--space-4)' }}>
                Clear Filters
              </button>
            )}
          </div>
        </>
      );
    }

    return (
      <>
        {/* Breadcrumb navigation - supports nested folders */}
        {currentFolderId && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-4)',
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleNavigateToRoot}
              className="btn btn-ghost btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-1) var(--space-2)' }}
            >
              <Home size={14} />
              Root
            </button>
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder.id}>
                <ChevronRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                {index === folderPath.length - 1 ? (
                  <span style={{ fontWeight: 500 }}>{folder.name}</span>
                ) : (
                  <button
                    onClick={() => handleNavigateToFolder(folder)}
                    className="btn btn-ghost btn-sm"
                    style={{ padding: 'var(--space-1) var(--space-2)' }}
                  >
                    {folder.name}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="masonry-grid">
          {/* Render folders (both at root and inside folders for nesting) */}
          {/* Render folders */}
          {folders.map(folder => (
            <FolderCard
              key={`folder-${folder.id}`}
              folder={folder}
              onOpen={handleOpenFolder}
              onRename={handleFolderRename}
              onDelete={(folder) => {
                setFolderToDelete(folder);
                setShowDeleteFolderConfirm(true);
              }}
              onDownload={handleDownloadFolder}
              onDrop={handleDropOnFolder}
              draggable={true}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          ))}

          {/* Render items */}
          {itemsToRender.map(item => (
            <LibraryItemCard
              key={item.id}
              item={item}
              onFavoriteToggle={item.isContextCreative ? undefined : handleFavoriteToggle}
              onTagClick={handleTagFilterClick}
              onDelete={item.isContextCreative ? undefined : handleDeleteClick}
              onView={isSelectionMode ? undefined : (item) => {
                setSelectedItem(item);
                setSelectedVersionPrompt('');
                setFullscreenIndex(null);
              }}
              onMoveToFolder={!isSelectionMode ? (item) => {
                setItemToMove(item);
                setShowMoveToFolderModal(true);
              } : undefined}
              onRemoveFromFolder={currentFolderId ? handleRemoveFromFolder : undefined}
              isInFolder={!!currentFolderId}
              selectable={isSelectionMode}
              isSelected={selectedItemIds.has(item.id)}
              onSelect={isSelectionMode ? handleToggleItemSelection : undefined}
              draggable={true}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              selectedItemIds={isSelectionMode ? selectedItemIds : undefined}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', gap: 0 }}>
      {/* Main Content Area */}
      <div style={{
        flex: 1,
        minWidth: 0,
        marginRight: selectedItem ? '480px' : 0,
        transition: 'margin-right 0.3s ease'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-8)' }}>
          <div>
            <h1>Asset Library</h1>
            <p className="text-secondary">Manage all your creative assets in one place.</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="btn btn-secondary"
              onClick={handleOneDriveClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                background: oneDriveConnected ? 'var(--color-bg-secondary)' : undefined,
                borderColor: oneDriveConnected ? '#0078D4' : undefined
              }}
            >
              <Cloud size={20} color={oneDriveConnected ? '#0078D4' : undefined} />
              {oneDriveConnected ? 'OneDrive' : 'Connect OneDrive'}
            </button>
            <div style={{ position: 'relative' }} ref={createMenuRef}>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                disabled={isUploading}
              >
                {isUploading ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
                {isUploading ? 'Uploading...' : 'Create New Asset'}
                {!isUploading && <ChevronDown size={14} style={{ marginLeft: 'var(--space-1)' }} />}
              </button>

              {showCreateMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 'var(--space-2)',
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 1000,
                  minWidth: '220px',
                  padding: 'var(--space-1) 0'
                }}>
                  <button
                    className="menu-item"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-3) var(--space-4)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => {
                      setShowCreateMenu(false);
                      navigate('/conversational');
                    }}
                  >
                    <MessageSquare size={18} color="var(--color-primary)" />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>Creative Chat</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Chat with AI to generate assets</div>
                    </div>
                  </button>
                  <button
                    className="menu-item"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-3) var(--space-4)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => {
                      setShowCreateMenu(false);
                      navigate('/generate');
                    }}
                  >
                    <Wand2 size={18} color="var(--color-primary)" />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>Generate with Prompt</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Create using a text prompt</div>
                    </div>
                  </button>
                  <div style={{ height: '1px', background: 'var(--color-border)', margin: 'var(--space-1) 0' }} />
                  <button
                    className="menu-item"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-3) var(--space-4)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => {
                      setShowCreateMenu(false);
                      setNewFolderName('');
                      setShowCreateFolderModal(true);
                    }}
                  >
                    <FolderPlus size={18} color="var(--color-primary)" />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>New Folder</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Organize assets into folders</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleFileUpload}
            />
          </div>
        </div>

        {/* Plan filter indicator */}
        {planIdFilter && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--info-50)',
            border: '1px solid var(--info-200)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Filter size={16} style={{ color: 'var(--info-600)' }} />
              <span style={{ color: 'var(--info-700)', fontSize: 'var(--font-size-sm)' }}>
                Showing creatives from content plan
              </span>
            </div>
            <button
              onClick={clearPlanIdFilter}
              className="btn btn-ghost btn-sm"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                color: 'var(--info-600)',
                padding: 'var(--space-1) var(--space-2)'
              }}
            >
              <X size={14} />
              Show All
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-1)',
          marginBottom: 'var(--space-6)',
          borderBottom: '1px solid var(--color-border-light)',
          paddingBottom: '0'
        }}>
          {LIBRARY_TABS.map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-3) var(--space-4)',
                  borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all var(--transition-fast)'
                }}
              >
                <TabIcon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filters & Search */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '400px' }}>
            <Search size={20} style={{
              position: 'absolute',
              left: 'var(--space-4)',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-tertiary)'
            }} />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              className="input-field"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 'var(--space-12)' }}
            />
          </div>

          {/* Favorites filter */}
          <button
            className={`btn ${filterFavorites ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilterFavorites(!filterFavorites)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            <Star size={16} fill={filterFavorites ? 'currentColor' : 'none'} />
            Favorites
          </button>

          {/* Tags filter */}
          {availableTags.length > 0 && (
            <div style={{ minWidth: '200px' }}>
              <TagSelector
                selectedTags={selectedTags}
                availableTags={availableTags}
                onTagsChange={setSelectedTags}
                allowCreate={false}
                placeholder="Filter by tags..."
              />
            </div>
          )}

          {/* Sort dropdown */}
          <select
            className="select-field"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ minWidth: '140px' }}
          >
            <option value="createdAt">Most Recent</option>
            <option value="usageCount">Most Used</option>
            <option value="lastUsedAt">Last Used</option>
          </select>

          {/* Tag manager button */}
          {availableTags.length > 0 && (
            <button
              className="btn btn-ghost"
              onClick={() => setShowTagManager(true)}
              title="Manage Tags"
            >
              <Settings size={18} />
            </button>
          )}

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              className="btn btn-ghost"
              onClick={clearFilters}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <X size={16} />
              Clear
            </button>
          )}

          {/* View mode toggle */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'auto' }}>
            {/* Selection mode toggle - available at root and inside folders */}
            <button
              className={`btn ${isSelectionMode ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleToggleSelectionMode}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
            >
              <Check size={16} />
              {isSelectionMode ? 'Cancel' : 'Select'}
            </button>
            <button
              className={`btn btn-icon ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={20} />
            </button>
            <button
              className={`btn btn-icon ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('list')}
            >
              <List size={20} />
            </button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {isSelectionMode && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              marginBottom: 'var(--space-4)',
              background: 'var(--color-primary-light)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-primary)'
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {selectedItemIds.size} item{selectedItemIds.size !== 1 ? 's' : ''} selected
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'auto' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleSelectAll}
              >
                Select All
              </button>
              {selectedItemIds.size > 0 && (
                <>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleClearSelection}
                  >
                    Clear
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowMoveToFolderModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                  >
                    <FolderInput size={16} />
                    Move to Folder
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {renderItems()}
      </div>{/* End Main Content Area */}

      {/* Right Side Panel (for creatives) */}
      {selectedItem && selectedItem.format && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '480px',
          backgroundColor: 'var(--color-bg-primary)',
          borderLeft: '1px solid var(--color-border)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
          animation: 'slideInRight 0.3s ease'
        }}>
          {/* Panel Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            borderBottom: '1px solid var(--color-border)'
          }}>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Creative Details</h3>
            <button
              onClick={() => setSelectedItem(null)}
              style={{
                background: 'var(--color-bg-secondary)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-primary)',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Panel Scrollable Content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* Image/Video Preview */}
            <div
              style={{
                aspectRatio: '16/9',
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: selectedItem.url && !selectedItem.isVideo ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (selectedItem.url && !selectedItem.isVideo) {
                  // For items without history, use index 0 (will fallback to url)
                  setFullscreenIndex(0);
                }
              }}
              title={selectedItem.url && !selectedItem.isVideo ? 'Click to view fullscreen' : undefined}
            >
              {selectedItem.isVideo && selectedItem.videoUrl ? (
                <video
                  src={selectedItem.videoUrl}
                  controls
                  autoPlay
                  loop
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : selectedItem.url ? (
                <img
                  src={selectedItem.url}
                  alt={selectedItem.title}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <div style={{ fontSize: '100px', color: 'var(--color-text-tertiary)' }}>
                  {selectedItem.brandName?.charAt(0)}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div style={{ padding: 'var(--space-4)' }}>
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <span className="badge badge-neutral">{selectedItem.format}</span>
                  {selectedItem.isVideo && (
                    <span className="badge badge-info" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Play size={12} /> Video
                    </span>
                  )}
                  <span className={`badge badge-${selectedItem.status === 'posted' ? 'success' : selectedItem.status === 'scheduled' ? 'info' : 'neutral'}`}>
                    {selectedItem.status}
                  </span>
                </div>
                <h2 style={{ marginBottom: 'var(--space-2)' }}>{selectedItem.title}</h2>
                <p className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                  Created for <strong style={{ color: 'var(--color-text-primary)' }}>{selectedItem.brandName}</strong>
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                {selectedItem.description && (
                  <div>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      Description
                    </h4>
                    <p style={{ fontSize: 'var(--font-size-sm)', lineHeight: '1.6' }}>
                      {selectedItem.description}
                    </p>
                  </div>
                )}

                {selectedItem.metadata?.imageMessage && (
                  <div>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      <MessageSquare size={14} style={{ display: 'inline', marginRight: 'var(--space-1)' }} />
                      Message on Image
                    </h4>
                    <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
                      "{selectedItem.metadata.imageMessage}"
                    </div>
                  </div>
                )}

                {selectedItem.metadata?.caption && (
                  <div>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      <Type size={14} style={{ display: 'inline', marginRight: 'var(--space-1)' }} />
                      Suggested Caption
                    </h4>
                    <p style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>
                      {selectedItem.metadata.caption}
                    </p>
                  </div>
                )}

                {selectedItem.metadata?.hashtags && (
                  <div>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      <Tag size={14} style={{ display: 'inline', marginRight: 'var(--space-1)' }} />
                      Hashtags
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      {selectedItem.metadata.hashtags.split(' ').map((tag, i) => (
                        <span key={i} style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-primary)',
                          background: 'var(--color-bg-tertiary)',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(selectedItem.pillarName || selectedItem.metadata?.pillarName) && (
                  <div>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      <Target size={14} style={{ display: 'inline', marginRight: 'var(--space-1)' }} />
                      Content Pillar
                    </h4>
                    <div style={{
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-bg-tertiary)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 500,
                      borderLeft: '3px solid var(--color-primary)'
                    }}>
                      {selectedItem.pillarName || selectedItem.metadata.pillarName}
                    </div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid var(--color-border-light)', paddingTop: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-4)' }}>
                    <Calendar size={14} />
                    Created on {formatDate(selectedItem.createdAt)}
                  </div>

                  {selectedItem.isVideo ? (
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={(e) => handleDownload(selectedItem.videoUrl, `${selectedItem.title}.mp4`, e)}
                    >
                      <Download size={18} />
                      Download Video
                    </button>
                  ) : (
                    <DownloadFormatDropdown
                      imageUrl={selectedItem.url}
                      filename={selectedItem.title}
                      buttonClassName="btn btn-primary"
                      buttonStyle={{ width: '100%' }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>{/* End scrollable content */}
        </div >
      )}

      {/* Right Side Panel (for library items: casting/locations/creatives) */}
      {
        selectedItem && selectedItem.type && (
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '480px',
            backgroundColor: 'var(--color-bg-primary)',
            borderLeft: '1px solid var(--color-border)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
            animation: 'slideInRight 0.3s ease'
          }}>
            {/* Panel Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              borderBottom: '1px solid var(--color-border)',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', textTransform: 'capitalize' }}>
                {selectedItem.type} Details
              </h3>
              <button
                onClick={handleCloseDetailModal}
                style={{
                  background: 'var(--color-bg-secondary)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Panel Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {/* Image / Carousel */}
              <div style={{
                width: '100%',
                aspectRatio: selectedItem.type === 'casting' ? '3/4' : selectedItem.type === 'carousel' ? '1/1' : '16/9',
                maxHeight: '300px',
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0 ? (
                  <>
                    {/* Currently selected carousel card */}
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setFullscreenIndex(selectedCarouselCardIndex);
                      }}
                    >
                      <img
                        src={selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl}
                        alt={selectedItem.carouselImages[selectedCarouselCardIndex]?.title || `Card ${selectedCarouselCardIndex + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                      {/* Fullscreen button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFullscreenIndex(selectedCarouselCardIndex);
                        }}
                        style={{
                          position: 'absolute',
                          top: 'var(--space-2)',
                          right: 'var(--space-2)',
                          background: 'rgba(0,0,0,0.7)',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="View fullscreen"
                      >
                        <Maximize2 size={16} />
                      </button>
                      {/* Card position indicator */}
                      <div style={{
                        position: 'absolute',
                        top: 'var(--space-2)',
                        left: 'var(--space-2)',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px'
                      }}>
                        {selectedCarouselCardIndex + 1}/{selectedItem.carouselImages.length}
                      </div>
                    </div>
                    {/* Carousel navigation arrows */}
                    {selectedItem.carouselImages.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCarouselCardIndex(prev => prev > 0 ? prev - 1 : selectedItem.carouselImages.length - 1);
                          }}
                          style={{
                            position: 'absolute',
                            left: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(0,0,0,0.6)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white'
                          }}
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCarouselCardIndex(prev => prev < selectedItem.carouselImages.length - 1 ? prev + 1 : 0);
                          }}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(0,0,0,0.6)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white'
                          }}
                        >
                          <ChevronRight size={18} />
                        </button>
                      </>
                    )}
                    {/* Carousel dots */}
                    <div style={{
                      position: 'absolute',
                      bottom: 'var(--space-2)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: '6px',
                      padding: '4px 8px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '12px'
                    }}>
                      {selectedItem.carouselImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCarouselCardIndex(idx);
                          }}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: idx === selectedCarouselCardIndex ? 'white' : 'rgba(255,255,255,0.5)',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                        />
                      ))}
                    </div>
                  </>
                ) : selectedItem.imageUrl ? (
                  <div
                    ref={imageContainerRef}
                    onMouseMove={handleMagnifierMove}
                    onMouseEnter={() => setShowMagnifier(true)}
                    onMouseLeave={() => setShowMagnifier(false)}
                    className="checkerboard-bg"
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'crosshair'
                    }}
                  >
                    <img
                      src={selectedItem.imageUrl}
                      alt={selectedItem.title || selectedItem.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                    {/* Hover magnifier lens */}
                    {showMagnifier && imageContainerRef.current && (() => {
                      const container = imageContainerRef.current;
                      const containerWidth = container.offsetWidth;
                      const containerHeight = container.offsetHeight;
                      const zoomFactor = 2.5;
                      const lensSize = 180;
                      const lensRadius = lensSize / 2;

                      // Calculate the zoomed image dimensions
                      const zoomedWidth = containerWidth * zoomFactor;
                      const zoomedHeight = containerHeight * zoomFactor;

                      // Calculate position: we want the cursor position to appear at the center of the lens
                      // The zoomed image position = lens_center - (cursor_position * zoom_factor)
                      const imgLeft = lensRadius - (magnifierPos.x * zoomFactor);
                      const imgTop = lensRadius - (magnifierPos.y * zoomFactor);

                      return (
                        <div
                          className="magnifier-lens"
                          style={{
                            position: 'absolute',
                            left: magnifierPos.x - lensRadius,
                            top: magnifierPos.y - lensRadius,
                            width: `${lensSize}px`,
                            height: `${lensSize}px`,
                            borderRadius: '50%',
                            border: '3px solid var(--color-primary)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                            pointerEvents: 'none',
                            overflow: 'hidden',
                            zIndex: 100,
                            background: '#000'
                          }}
                        >
                          <img
                            src={selectedItem.imageUrl}
                            alt=""
                            style={{
                              position: 'absolute',
                              width: zoomedWidth,
                              height: zoomedHeight,
                              maxWidth: 'none',
                              left: imgLeft,
                              top: imgTop,
                              objectFit: 'contain',
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      );
                    })()}
                    {/* Fullscreen button */}
                    <button
                      onClick={() => {
                        // Find the index of the current image in history
                        const currentIndex = selectedItem.history?.findIndex(v => v.imageUrl === selectedItem.imageUrl) ?? 0;
                        setFullscreenIndex(currentIndex >= 0 ? currentIndex : 0);
                      }}
                      onMouseEnter={() => setShowMagnifier(false)}
                      style={{
                        position: 'absolute',
                        top: 'var(--space-2)',
                        left: 'var(--space-2)',
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        color: 'white',
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        zIndex: 200
                      }}
                      title="View fullscreen"
                    >
                      <Maximize2 size={14} />
                      Fullscreen
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: '100px', color: 'var(--color-text-tertiary)' }}>
                    {(selectedItem.title || selectedItem.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Upscale Controls - Show for all images */}
              {(selectedItem.imageUrl || (selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0)) && (
                <div style={{
                  borderBottom: '1px solid var(--color-border-light)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--color-bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <ArrowUpRight size={18} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>Upscale for Print</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <select
                      value={selectedUpscaleFactor}
                      onChange={(e) => setSelectedUpscaleFactor(e.target.value)}
                      disabled={isUpscaling}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        fontSize: 'var(--font-size-sm)',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="x2">2x</option>
                      <option value="x3">3x</option>
                      <option value="x4">4x</option>
                    </select>
                    <button
                      onClick={() => setShowUpscaleConfirm(true)}
                      disabled={isUpscaling}
                      className="btn btn-sm"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: 'var(--radius-sm)',
                        cursor: isUpscaling ? 'wait' : 'pointer',
                        opacity: isUpscaling ? 0.7 : 1
                      }}
                    >
                      {isUpscaling ? (
                        <>
                          <Loader2 size={14} className="spin" />
                          Upscaling...
                        </>
                      ) : (
                        <>
                          <ArrowUpRight size={14} />
                          Upscale (~{UPSCALE_CREDITS.toLocaleString()} credits)
                        </>
                      )}
                    </button>
                  </div>
                  {upscaleError && (
                    <div style={{
                      color: 'var(--color-error)',
                      fontSize: 'var(--font-size-xs)',
                      marginTop: 'var(--space-1)'
                    }}>
                      {upscaleError}
                    </div>
                  )}
                </div>
              )}

              {/* Refinement Panel - Show for all images (single images and carousels) */}
              {(selectedItem.imageUrl || (selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0)) && (
                <div style={{
                  borderBottom: '1px solid var(--color-border-light)',
                  padding: 'var(--space-4)',
                  background: 'var(--color-bg-secondary)'
                }}>
                  {/* Toggle button */}
                  <button
                    onClick={() => setShowRefinementPanel(!showRefinementPanel)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <Wand2 size={18} />
                      <span style={{ fontWeight: 500 }}>
                        {selectedItem.type === 'carousel' ? 'Refine Carousel Cards' : 'Refine Image'}
                      </span>
                      {selectedItem.type !== 'carousel' && selectedItem.history?.length > 1 && (
                        <span className="badge badge-info" style={{ fontSize: '10px' }}>
                          {selectedItem.history.length} versions
                        </span>
                      )}
                      {selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0 && (
                        <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                          {selectedItem.carouselImages.length} cards
                        </span>
                      )}
                    </div>
                    {showRefinementPanel ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>

                  {/* Expanded refinement panel */}
                  {showRefinementPanel && (
                    <div style={{ marginTop: 'var(--space-4)' }}>
                      {/* Carousel card selector */}
                      {selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0 && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                          <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-2)'
                          }}>
                            Select card to refine:
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: 'var(--space-2)',
                            overflowX: 'auto',
                            paddingBottom: 'var(--space-2)'
                          }}>
                            {selectedItem.carouselImages.map((card, idx) => (
                              <div
                                key={`selector-${idx}-${card.imageUrl?.slice(-20) || idx}`}
                                onClick={() => setSelectedCarouselCardIndex(idx)}
                                style={{
                                  flexShrink: 0,
                                  position: 'relative',
                                  borderRadius: 'var(--radius-md)',
                                  border: selectedCarouselCardIndex === idx
                                    ? '2px solid var(--color-primary)'
                                    : '2px solid var(--color-border)',
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  opacity: selectedCarouselCardIndex === idx ? 1 : 0.7,
                                  transition: 'all var(--transition-fast)'
                                }}
                              >
                                <img
                                  src={card.imageUrl}
                                  alt={card.headline || `Card ${idx + 1}`}
                                  style={{
                                    width: '80px',
                                    height: '80px',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                />
                                <div style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  background: selectedCarouselCardIndex === idx
                                    ? 'var(--color-primary)'
                                    : 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  padding: '2px 4px',
                                  textAlign: 'center'
                                }}>
                                  Card {idx + 1}
                                </div>
                                {card.history?.length > 1 && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    background: 'var(--info-600)',
                                    color: 'white',
                                    fontSize: '8px',
                                    padding: '1px 4px',
                                    borderRadius: '4px'
                                  }}>
                                    {card.history.length}v
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Refinement input using reusable component */}
                      <ImageRefinementPanel
                        onRefine={handleRefineImage}
                        isRefining={isRefining}
                        error={refinementError}
                        statusMessage={refinementStatus}
                        compact
                        initialPrompt={selectedVersionPrompt}
                        userId={currentUser?.uid}
                        brandId={selectedBrand?.id}
                        originalImageUrl={selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0
                          ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
                          : selectedItem.imageUrl}
                        originalPrompt={selectedVersionPrompt || selectedItem.description}
                        onObjectEdit={() => {
                          setInpaintMode('replace');
                          setShowMaskDrawer(true);
                        }}
                        onRemoveObject={() => {
                          setInpaintMode('remove');
                          setShowMaskDrawer(true);
                        }}
                        onCrop={() => setShowCropCanvas(true)}
                        onRemoveBackground={handleRemoveBackground}
                        isInpainting={isInpainting}
                        isRemovingBackground={isRemovingBackground}
                        bgRemovalError={bgRemovalError}
                        inpaintError={inpaintError}
                      />

                      {/* Success message */}
                      {refinementSuccess && (
                        <div style={{
                          padding: 'var(--space-3)',
                          background: 'var(--success-50)',
                          color: 'var(--success-700)',
                          borderRadius: 'var(--radius-md)',
                          marginTop: 'var(--space-2)',
                          fontSize: 'var(--font-size-sm)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)'
                        }}>
                          <Sparkles size={16} />
                          Image refined successfully! Check the version history below.
                        </div>
                      )}

                      {/* History thumbnail strip for carousel cards */}
                      {selectedItem.type === 'carousel' && selectedItem.carouselImages?.[selectedCarouselCardIndex]?.history?.length > 0 && (
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 'var(--space-2)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                              <Clock size={12} />
                              Card {selectedCarouselCardIndex + 1} Version History ({selectedItem.carouselImages[selectedCarouselCardIndex].history.length})
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: 'var(--space-2)',
                            overflowX: 'auto',
                            paddingBottom: 'var(--space-2)'
                          }}>
                            {selectedItem.carouselImages[selectedCarouselCardIndex].history.map((version, idx) => {
                              const currentCardImageUrl = selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl;
                              const isActive = currentCardImageUrl === version.imageUrl;
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    flexShrink: 0,
                                    position: 'relative',
                                    borderRadius: 'var(--radius-md)',
                                    border: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <img
                                    src={version.imageUrl}
                                    alt={`Version ${idx + 1}`}
                                    style={{
                                      width: '64px',
                                      height: '64px',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      opacity: isActive ? 1 : 0.7
                                    }}
                                    onClick={async () => {
                                      // Set this version as active for the carousel card
                                      try {
                                        const updatedCarouselImages = [...selectedItem.carouselImages];
                                        updatedCarouselImages[selectedCarouselCardIndex] = {
                                          ...updatedCarouselImages[selectedCarouselCardIndex],
                                          imageUrl: version.imageUrl
                                        };
                                        const updatedItem = {
                                          ...selectedItem,
                                          carouselImages: updatedCarouselImages,
                                          // Update main imageUrl if first card
                                          ...(selectedCarouselCardIndex === 0 && { imageUrl: version.imageUrl })
                                        };
                                        // Update Firestore
                                        await updateLibraryItem(selectedItem.id, {
                                          carouselImages: updatedCarouselImages,
                                          ...(selectedCarouselCardIndex === 0 && { imageUrl: version.imageUrl })
                                        }, currentUser.uid);
                                        // Update local state
                                        setSelectedItem(updatedItem);
                                        setLibraryItems(prev =>
                                          prev.map(i => i.id === selectedItem.id ? updatedItem : i)
                                        );
                                      } catch (err) {
                                        console.error('Failed to set active version:', err);
                                      }
                                    }}
                                    title={version.prompt || 'Original'}
                                  />
                                  {/* Original badge */}
                                  {version.isOriginal && (
                                    <div style={{
                                      position: 'absolute',
                                      bottom: '2px',
                                      left: '2px',
                                      background: 'var(--color-primary)',
                                      color: 'white',
                                      fontSize: '8px',
                                      padding: '1px 4px',
                                      borderRadius: '4px'
                                    }}>
                                      Original
                                    </div>
                                  )}
                                  {/* Active indicator */}
                                  {isActive && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: '2px',
                                      background: 'var(--color-primary)',
                                      borderRadius: '50%',
                                      width: '12px',
                                      height: '12px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <Check size={8} color="white" />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* History thumbnail strip for single images */}
                      {selectedItem.type !== 'carousel' && selectedItem.history && selectedItem.history.length > 0 && (
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 'var(--space-2)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                              <Clock size={12} />
                              Version History ({selectedItem.history.length})
                            </div>
                            {selectedItem.history.length > 1 && (
                              <button
                                onClick={handleDownloadAllVersions}
                                disabled={isDownloadingAll}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 'var(--space-1)',
                                  padding: '4px 8px',
                                  background: 'var(--color-bg-tertiary)',
                                  border: 'none',
                                  borderRadius: 'var(--radius-sm)',
                                  color: 'var(--color-text-secondary)',
                                  cursor: isDownloadingAll ? 'wait' : 'pointer',
                                  fontSize: 'var(--font-size-xs)',
                                  opacity: isDownloadingAll ? 0.7 : 1
                                }}
                                title="Download all versions as ZIP"
                              >
                                {isDownloadingAll ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Archive size={12} />
                                )}
                                {isDownloadingAll ? 'Zipping...' : 'Download All'}
                              </button>
                            )}
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: 'var(--space-2)',
                            overflowX: 'auto',
                            paddingBottom: 'var(--space-2)'
                          }}>
                            {selectedItem.history.map((version, idx) => {
                              const isActive = selectedItem.imageUrl === version.imageUrl;
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    flexShrink: 0,
                                    position: 'relative',
                                    borderRadius: 'var(--radius-md)',
                                    border: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <img
                                    src={version.imageUrl}
                                    alt={`Version ${idx + 1}`}
                                    style={{
                                      width: '64px',
                                      height: '64px',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      opacity: isActive ? 1 : 0.7
                                    }}
                                    onClick={() => handleSetActiveVersion(idx)}
                                    title={version.prompt || 'Original'}
                                  />
                                  {/* Original badge */}
                                  {version.isOriginal && (
                                    <div style={{
                                      position: 'absolute',
                                      bottom: '2px',
                                      left: '2px',
                                      background: 'rgba(0,0,0,0.7)',
                                      color: 'white',
                                      fontSize: '8px',
                                      padding: '1px 4px',
                                      borderRadius: '4px'
                                    }}>
                                      Original
                                    </div>
                                  )}
                                  {/* Upscale badge */}
                                  {version.isUpscaled && (
                                    <div style={{
                                      position: 'absolute',
                                      bottom: '2px',
                                      right: '2px',
                                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                      color: 'white',
                                      fontSize: '8px',
                                      padding: '1px 4px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '2px'
                                    }}>
                                      <ArrowUpRight size={8} />
                                      {version.upscaleMetadata?.factor || 'HD'}
                                    </div>
                                  )}
                                  {/* Active indicator */}
                                  {isActive && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: '2px',
                                      background: 'var(--color-primary)',
                                      borderRadius: '50%',
                                      width: '8px',
                                      height: '8px'
                                    }} />
                                  )}
                                  {/* Split button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSplitClick(idx);
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: '2px',
                                      left: '2px',
                                      background: 'rgba(0,0,0,0.7)',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '2px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    title="Split to new item"
                                  >
                                    <Split size={10} color="white" />
                                  </button>
                                  {/* Delete button - only for non-original, non-active versions */}
                                  {!version.isOriginal && !isActive && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteVersionClick(idx);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        top: '2px',
                                        right: '2px',
                                        background: 'rgba(220,38,38,0.9)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '2px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      title="Delete this version"
                                    >
                                      <Trash2 size={10} color="white" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginTop: 'var(--space-1)'
                          }}>
                            Click a version to use it • <Split size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> split to new item • <Trash2 size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> delete version
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Details */}
              <div style={{ padding: 'var(--space-6)', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                      <span className="badge badge-neutral" style={{ textTransform: 'capitalize' }}>
                        {selectedItem.type}
                      </span>
                      {!isEditing && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={handleStartEdit}
                          style={{ padding: 'var(--space-1)' }}
                          title="Edit item"
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        style={{
                          width: '100%',
                          fontSize: 'var(--font-size-xl)',
                          fontWeight: 600,
                          padding: 'var(--space-2)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)'
                        }}
                        placeholder="Title"
                      />
                    ) : (
                      <h2 style={{ margin: 0 }}>{selectedItem.title || selectedItem.name}</h2>
                    )}
                  </div>
                  <FavoriteButton
                    isFavorite={selectedItem.isFavorite}
                    onToggle={() => handleFavoriteToggle(selectedItem)}
                    size={24}
                  />
                </div>

                {/* Description - editable */}
                {isEditing ? (
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
                      <label style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)' }}>
                        Description
                      </label>
                      {/* AI Generate Button */}
                      <button
                        onClick={handleGenerateMetadata}
                        disabled={isGeneratingMetadata || !selectedItem?.imageUrl}
                        className="btn btn-ghost btn-sm"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)',
                          padding: '4px 8px',
                          fontSize: 'var(--font-size-xs)',
                          color: isGeneratingMetadata ? 'var(--color-text-tertiary)' : 'var(--color-primary)'
                        }}
                        title="Generate title and description with AI using brand context"
                      >
                        {isGeneratingMetadata ? (
                          <>
                            <Loader2 size={12} className="animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles size={12} />
                            AI Generate
                          </>
                        )}
                      </button>
                    </div>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: 'var(--space-2)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)'
                      }}
                      placeholder="Add a description with hashtags..."
                    />
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                      Tip: Use AI Generate to create a social media caption with hashtags based on the image and brand
                    </div>
                  </div>
                ) : selectedItem.description ? (
                  <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                    {selectedItem.description}
                  </p>
                ) : null}

                {/* Carousel Caption - editable */}
                {selectedItem.type === 'carousel' && (isEditing || selectedItem.caption) && (
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      Caption
                    </h4>
                    {isEditing ? (
                      <textarea
                        value={editForm.caption}
                        onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                        style={{
                          width: '100%',
                          minHeight: '100px',
                          padding: 'var(--space-2)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          fontSize: 'var(--font-size-sm)',
                          background: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)'
                        }}
                        placeholder="Add a caption..."
                      />
                    ) : (
                      <div style={{
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        whiteSpace: 'pre-wrap',
                        position: 'relative'
                      }}>
                        {selectedItem.caption}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(selectedItem.fullPost || selectedItem.caption);
                          }}
                          style={{
                            position: 'absolute',
                            top: 'var(--space-2)',
                            right: 'var(--space-2)',
                            fontSize: 'var(--font-size-xs)'
                          }}
                          title="Copy caption"
                        >
                          Copy
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Carousel Hashtags - editable */}
                {selectedItem.type === 'carousel' && (isEditing || selectedItem.hashtags) && (
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                      Hashtags
                    </h4>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.hashtags}
                        onChange={(e) => setEditForm({ ...editForm, hashtags: e.target.value })}
                        style={{
                          width: '100%',
                          padding: 'var(--space-2)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--font-size-sm)',
                          background: 'var(--color-bg-primary)',
                          color: 'var(--color-primary)'
                        }}
                        placeholder="#hashtag1 #hashtag2"
                      />
                    ) : (
                      <div style={{
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-primary)'
                      }}>
                        {selectedItem.hashtags}
                      </div>
                    )}
                  </div>
                )}

                {/* Edit mode action buttons */}
                {isEditing && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveEdit}
                      disabled={isSaving}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={14} />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Tags */}
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <h4 style={{ fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>
                    Tags
                  </h4>
                  <TagSelector
                    selectedTags={selectedItem.tags || []}
                    availableTags={availableTags}
                    onTagsChange={(newTags) => {
                      setItemToTag(selectedItem);
                      handleUpdateItemTags(newTags);
                      setSelectedItem({ ...selectedItem, tags: newTags });
                    }}
                    onCreateTag={async (tagName) => {
                      // Tag will be created when added
                    }}
                    placeholder="Add tags..."
                  />
                </div>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Used</div>
                    <div style={{ fontWeight: 600 }}>{selectedItem.usageCount || 0} times</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Created</div>
                    <div style={{ fontWeight: 600 }}>
                      {selectedItem.createdAt && formatDate(selectedItem.createdAt.toDate ? selectedItem.createdAt.toDate() : selectedItem.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <DownloadFormatDropdown
                    imageUrl={selectedItem.imageUrl}
                    filename={selectedItem.title || selectedItem.name}
                    buttonClassName="btn btn-primary"
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      handleDeleteClick(selectedItem);
                      setSelectedItem(null);
                    }}
                    style={{ color: 'var(--error-600)' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Tag Manager Modal */}
      <TagManagerModal
        isOpen={showTagManager}
        onClose={() => setShowTagManager(false)}
        tags={availableTags}
        onRenameTag={handleRenameTag}
        onDeleteTag={handleDeleteTag}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete from Library?"
        message={`Are you sure you want to delete "${itemToDelete?.title || itemToDelete?.name || 'this item'}" from your library? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Split Version Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSplitConfirm}
        onClose={() => {
          setShowSplitConfirm(false);
          setVersionToSplit(null);
        }}
        onConfirm={handleConfirmSplit}
        title="Split to New Item?"
        message="This will create a new library item from this version. The new item will be linked to the original and include the same title, description, and tags."
        confirmText="Split"
        cancelText="Cancel"
        variant="primary"
      />

      {/* Delete Version Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteVersionConfirm}
        onClose={() => {
          setShowDeleteVersionConfirm(false);
          setVersionToDelete(null);
        }}
        onConfirm={handleConfirmDeleteVersion}
        title="Delete This Version?"
        message="This will permanently delete this refined version from the history. The original and other versions will remain. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Upscale Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUpscaleConfirm}
        onClose={() => setShowUpscaleConfirm(false)}
        onConfirm={handleUpscaleImage}
        title="Upscale Image?"
        message={`Upscale this image ${selectedUpscaleFactor}?`}
        confirmText={isUpscaling ? 'Upscaling...' : 'Upscale'}
        cancelText="Cancel"
        variant="primary"
      />

      {/* OneDrive Connect Modal */}
      <OneDriveConnectModal
        isOpen={showOneDriveConnect}
        onClose={() => setShowOneDriveConnect(false)}
        brand={selectedBrand}
        onSuccess={handleOneDriveConnected}
      />

      {/* OneDrive Browser Modal */}
      <OneDriveBrowserModal
        isOpen={showOneDriveBrowser}
        onClose={() => setShowOneDriveBrowser(false)}
        brand={selectedBrand}
        onSelect={handleOneDriveSelect}
      />

      {/* Ad Creation Modal */}
      <AdCreationModal
        isOpen={showAdModal}
        onClose={() => setShowAdModal(false)}
        userId={currentUser?.uid}
        brandId={selectedBrand?.id}
        onSuccess={() => {
          loadItems();
          loadTags(); // Refresh tags as well
          if (activeTab !== 'ads') setActiveTab('ads');
        }}
      />

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowCreateFolderModal(false)}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: 'var(--space-6)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 'var(--space-4)' }}>Create New Folder</h3>
            <input
              type="text"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
              }}
              autoFocus
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--font-size-sm)'
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateFolderModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || !currentUser?.uid || !selectedBrand?.id || isCreatingFolder}
              >
                {isCreatingFolder ? (
                  <>
                    <Loader2 size={16} className="animate-spin" style={{ marginRight: '8px' }} />
                    Creating...
                  </>
                ) : (
                  'Create Folder'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameFolderModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowRenameFolderModal(false)}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: 'var(--space-6)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 'var(--space-4)' }}>Rename Folder</h3>
            <input
              type="text"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameFolder();
              }}
              autoFocus
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--font-size-sm)'
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowRenameFolderModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRenameFolder}
                disabled={!newFolderName.trim()}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteFolderConfirm}
        onClose={() => {
          setShowDeleteFolderConfirm(false);
          setFolderToDelete(null);
        }}
        onConfirm={handleConfirmDeleteFolder}
        title="Delete Folder?"
        message={`Are you sure you want to delete the folder "${folderToDelete?.name}"? Items inside will be moved back to the root level.`}
        confirmText="Delete Folder"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Move to Folder Modal */}
      {showMoveToFolderModal && (
        <MoveToFolderModal
          isOpen={showMoveToFolderModal}
          onClose={() => {
            setShowMoveToFolderModal(false);
            setItemToMove(null);
          }}
          onSelectFolder={handleMoveToFolder}
          folders={folders}
          currentFolderId={currentFolderId}
          itemCount={selectedItemIds.size > 0 ? selectedItemIds.size : 1}
          itemName={itemToMove?.title || itemToMove?.name}
          isMoving={isMovingItem}
          userId={currentUser?.uid}
          brandId={selectedBrand?.id}
          activeTab={activeTab}
        />
      )}

      {
        showMaskDrawer && selectedItem && (
          <MaskDrawingCanvas
            imageUrl={
              selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0
                ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
                : selectedItem.imageUrl
            }
            onMaskComplete={handleInpaint}
            onCancel={() => setShowMaskDrawer(false)}
            initialPrompt={inpaintPrompt}
            initialMode={inpaintMode}
          />
        )
      }

      {/* Image Crop Canvas */}
      {
        showCropCanvas && selectedItem && (
          <ImageCropCanvas
            imageUrl={
              selectedItem.type === 'carousel' && selectedItem.carouselImages?.length > 0
                ? selectedItem.carouselImages[selectedCarouselCardIndex]?.imageUrl
                : selectedItem.imageUrl
            }
            onCropComplete={handleCropComplete}
            onCancel={() => setShowCropCanvas(false)}
          />
        )
      }

      {/* Fullscreen Image Viewer with Navigation */}
      {
        fullscreenImageUrl && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setFullscreenIndex(null)}
            onMouseMove={handleFullscreenMouseActivity}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchStartX.current === null) return;
              const touchEndX = e.changedTouches[0].clientX;
              const diff = touchStartX.current - touchEndX;
              if (Math.abs(diff) > 50) { // Minimum swipe distance
                if (diff > 0) {
                  navigateFullscreen('next');
                } else {
                  navigateFullscreen('prev');
                }
              }
              touchStartX.current = null;
            }}
          >
            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); setFullscreenIndex(null); }}
              style={{
                position: 'absolute',
                top: 'var(--space-6)',
                right: 'var(--space-6)',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              title="Close (ESC)"
            >
              <X size={24} />
            </button>

            {/* Left arrow navigation */}
            {fullscreenHistory.length > 1 && fullscreenIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateFullscreen('prev'); }}
                style={{
                  position: 'absolute',
                  left: 'var(--space-6)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                title="Previous (←)"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {/* Right arrow navigation */}
            {fullscreenHistory.length > 1 && fullscreenIndex < fullscreenHistory.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateFullscreen('next'); }}
                style={{
                  position: 'absolute',
                  right: 'var(--space-6)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                title="Next (→)"
              >
                <ChevronRight size={24} />
              </button>
            )}

            {/* Auto-hiding thumbnail strip and controls */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: 'var(--space-4)',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-3)',
                opacity: showFullscreenThumbs ? 1 : 0,
                transform: showFullscreenThumbs ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                pointerEvents: showFullscreenThumbs ? 'auto' : 'none'
              }}
              onMouseEnter={() => setShowFullscreenThumbs(true)}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Thumbnail strip */}
              {fullscreenHistory.length > 1 && (
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  justifyContent: 'center',
                  maxWidth: '90vw',
                  overflowX: 'auto',
                  padding: 'var(--space-2) 0'
                }}>
                  {fullscreenHistory.map((version, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setFullscreenIndex(idx);
                        // Sync carousel card index when clicking thumbnail for carousel
                        if (isCarouselFullscreen) {
                          setSelectedCarouselCardIndex(idx);
                        }
                      }}
                      style={{
                        flexShrink: 0,
                        width: '56px',
                        height: '56px',
                        padding: 0,
                        border: idx === fullscreenIndex ? '2px solid white' : '2px solid transparent',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        opacity: idx === fullscreenIndex ? 1 : 0.6,
                        transition: 'all 0.2s ease',
                        background: 'transparent'
                      }}
                    >
                      <img
                        src={version.imageUrl}
                        alt={isCarouselFullscreen ? `Card ${idx + 1}` : `Version ${idx + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Position indicator and keyboard hints */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                color: 'rgba(255,255,255,0.6)',
                fontSize: 'var(--font-size-xs)'
              }}>
                {fullscreenHistory.length > 1 && (
                  <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                    {isCarouselFullscreen ? 'Card ' : ''}{fullscreenIndex + 1} / {fullscreenHistory.length}
                  </span>
                )}
                {fullscreenHistory.length > 1 && (
                  <span>
                    <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 6px', borderRadius: '3px', marginRight: '4px' }}>←</kbd>
                    <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 6px', borderRadius: '3px' }}>→</kbd>
                    <span style={{ marginLeft: '4px' }}>navigate</span>
                  </span>
                )}
                <span>
                  <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 6px', borderRadius: '3px' }}>ESC</kbd>
                  <span style={{ marginLeft: '4px' }}>close</span>
                </span>
              </div>
            </div>

            {/* Full size image with checkerboard for transparency */}
            <div
              className="checkerboard-bg"
              style={{
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 0 60px rgba(0,0,0,0.5)',
                display: 'inline-block'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={fullscreenImageUrl}
                alt={`Version ${fullscreenIndex + 1}`}
                style={{
                  maxWidth: '85vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          </div>
        )
      }
    </div >
  );
};

export default AssetLibrary;
